import { UserInfo } from '@mango-engineering/maknet-client-sdk/lib/shop/makroUser/types';
import { ProductUnavailableReasonCodes } from '@mango-engineering/maknet-client-sdk/lib/shop/product/contract/product.contract';
import {
  getProductStatus,
  isProductInStock,
  ProductAvailability,
} from '@mango-engineering/maknet-client-sdk/lib/shop/product/util/statusUtils';
import { config } from '../config/env';
import { InjectedSearchData } from '../types/common';
import { HomeRecommendationProducts, RecommendationProductIds, RecommendationType } from '../types/common';
import { Product } from '../types/product.types';
import { PRODUCT_ID_PREFIX, PRODUCT_TYPE_1P, PRODUCT_TYPE_3P, RecommendationVariants } from './constants';
import {
  getProductIdWithoutPrefix,
  PROPOINT_COLLECTION,
  ComplexPromotionType,
  TRANSLATION_KEYS,
  PROMOTION_COLORS,
  DEFAULT,
} from './product.util';

export const isExpressDeliveryProduct = (product: Product): boolean => {
  const isExpressDelivery = (product?.variants && product?.variants[0]?.isExpressDelivery) ?? false;
  return isExpressDelivery;
};

export const getProductAvailability = (
  product: Product,
  storeCodes: string[],
  userInfo?: UserInfo,
  isExpressMode?: boolean,
  isAlcoholSaleProhibited?: boolean,
  countryCode?: string,
  hasAlcoholProductAvailability?: boolean,
) => {
  let status =
    product.unavailableReason?.code === ProductUnavailableReasonCodes.UNAVAILABLE_IN_RM3
      ? ProductAvailability.unavailable
      : getProductStatus(
          product,
          storeCodes,
          config.ENABLE_3P_OWNFLEET,
          userInfo,
          isExpressMode,
          isAlcoholSaleProhibited,
          undefined,
          undefined,
          countryCode,
        );
  const isExpressDeliveryProducts = isExpressDeliveryProduct(product);

  if (
    (status === ProductAvailability.notEligible || status === ProductAvailability.restricted) &&
    hasAlcoholProductAvailability === false
  ) {
    // Countries without alcohol validation treat SDK restriction statuses as available.
    status = ProductAvailability.available;
  }

  return {
    status,
    isAvailable: status === ProductAvailability.available,
    isOutOfStock: status === ProductAvailability.outOfStock,
    isNotDeliverable: status === ProductAvailability.notDeliverable,
    isNotEligible: status === ProductAvailability.notEligible,
    isUnavailable: status === ProductAvailability.unavailable,
    isDisabled: status !== ProductAvailability.available,
    isRestricted: status === ProductAvailability.restricted,
    isExpressAvailable: isExpressMode && isExpressDeliveryProducts,
  };
};

export const checkPlatform = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/android/.test(userAgent)) {
    return 'android';
  } else if (/iphone|ipod|ipad/.test(userAgent)) {
    return 'ios';
  } else {
    return 'unknown';
  }
};

export const getBasketRecommendationTitle = (typeOfRecommendation?: string, isHorizonUiUxEnabled?: boolean) => {
  if (!typeOfRecommendation) return '';
  if (isHorizonUiUxEnabled) {
    return 'carousel.title.basketRecommendation';
  }
  switch (typeOfRecommendation) {
    case RecommendationType.CUSTOM:
      return 'carousel.title.customListTitle';
    case RecommendationType.PERSONALIZED:
      return 'carousel.title.personalizedListTitle';
    default:
      return 'carousel.title.customListFor3PTitle';
  }
};

export const getRecommendationPieceResult = (
  ProductIds: string[],
  product: Product[],
  storeCodes: string[],
  recommdedProducts?: RecommendationProductIds[],
  hasAlcoholProductAvailability?: boolean,
) => {
  if (recommdedProducts) {
    const updatedProducts = recommdedProducts.map((item) => {
      const itemID = `${PRODUCT_ID_PREFIX}${item.productId}`;
      const productTemp = product.find((prod: Product | null) => {
        return prod?.id === itemID;
      });
      return {
        ...productTemp,
        similarityScore: item?.similarity,
        vectorDistance: item?.vector_distance,
      };
    });
    const actualResp = updatedProducts.filter((p) => {
      return (
        isProductInStock(p as Product) &&
        getProductAvailability(
          p as Product,
          storeCodes,
          undefined,
          undefined,
          undefined,
          undefined,
          hasAlcoholProductAvailability,
        ).isAvailable
      );
    });
    return actualResp;
  } else {
    const updatedProducts = ProductIds.map((item) => {
      const itemID = `${PRODUCT_ID_PREFIX}${item}`;
      const productTemp = product.find((prod: Product | null) => {
        return prod?.id === itemID;
      });
      return productTemp;
    });
    const actualResp = updatedProducts.filter((p) => {
      return (
        isProductInStock(p as Product) &&
        getProductAvailability(
          p as Product,
          storeCodes,
          undefined,
          undefined,
          undefined,
          undefined,
          hasAlcoholProductAvailability,
        ).isAvailable
      );
    });
    return actualResp;
  }
};

export const hasMoreProduct = (found?: number, productLength?: number) => {
  return Boolean(found && productLength && productLength < found);
};

export const getVariant = (variant?: string) => {
  if (variant === RecommendationType.BASKET_RECOMMENDATION) {
    return RecommendationVariants.BASKET;
  } else if (variant === RecommendationType.TOPPICKS || variant === RecommendationType.SEARCH_START_TOP_PICKS) {
    return RecommendationVariants.TOP_PICKS;
  } else if (variant === RecommendationType.BUY_AGAIN) {
    return RecommendationVariants.BUY_AGAIN;
  }

  return RecommendationVariants.RECOMMENDED_FOR_YOU;
};

export const getRecommendationPieceResultV2 = (
  recommendProducts: HomeRecommendationProducts[],
  product: Product[],
  storeCodes: string[],
  isExpressMode?: boolean,
  hasAlcoholProductAvailability?: boolean,
) => {
  if (recommendProducts?.length > 0) {
    const bSortedProducts = recommendProducts.map((item) => {
      const itemID = `${PRODUCT_ID_PREFIX}${item.id}`;
      const productTemp = product.find((prod: Product | null) => {
        return prod?.id === itemID;
      });
      return {
        ...productTemp,
        alreadyPurchased: item?.alreadyPurchased,
        recommendationScore: item?.recommendationScore,
        normalisedRecommendationScore: item?.normalisedRecommendationScore,
      };
    });
    const actualResp = bSortedProducts.filter((p) => {
      return (
        isProductInStock(p as Product) &&
        getProductAvailability(
          p as Product,
          storeCodes,
          undefined,
          isExpressMode,
          undefined,
          undefined,
          hasAlcoholProductAvailability,
        ).isAvailable
      );
    });
    return actualResp;
  } else {
    const Resp = product.filter((p) => {
      return (
        isProductInStock(p as Product) &&
        getProductAvailability(
          p as Product,
          storeCodes,
          undefined,
          isExpressMode,
          undefined,
          undefined,
          hasAlcoholProductAvailability,
        ).isAvailable
      );
    });
    return Resp;
  }
};

const is1PMakroStore = (storeCode: string | undefined) => storeCode && !storeCode.includes(PRODUCT_TYPE_3P);
const getProductStoreCode = (product: Product): string | undefined => {
  const storeCode = product?.storeCode || (product?.variants && product?.variants[0]?.storeCode);
  return storeCode;
};

export const getAnalyticsProducts = (products: Product[]) => {
  return products?.map((product) => {
    const storeCode = getProductStoreCode(product);
    const is1Pproduct = is1PMakroStore(storeCode);
    return {
      id: product.id,
      sku:
        (product as Product)?.variants?.[0]?.sku !== undefined
          ? ((product as Product)?.variants?.[0]?.sku ?? '')
          : (product?.sku ?? ''),
      skuCode: product.skuCode || '',
      productType: is1Pproduct ? PRODUCT_TYPE_1P : PRODUCT_TYPE_3P,
      makroId: product?.makroId ? product.makroId : product.sku,
      variantId: product?.variants?.[0]?.id ?? '',
      ...(product?.recommendationScore !== undefined && {
        recommendationScore: product?.recommendationScore,
      }),
      ...(product?.normalisedRecommendationScore !== undefined && {
        normalisedRecommendationScore: product?.normalisedRecommendationScore,
      }),
      ...(product?.alreadyPurchased !== undefined && {
        alreadyPurchased: product?.alreadyPurchased,
      }),
      ...(product?.similarityScore !== undefined && {
        similarityScore: product?.similarityScore,
      }),
      ...(product?.vectorDistance !== undefined && {
        vectorDistance: product?.vectorDistance,
      }),
      ...(product?.isSponsored !== undefined && {
        isSponsored: product?.isSponsored,
      }),
    };
  });
};

export const isFlagEnabledForCountry = (countryCode: string, config: { [key: string]: string | boolean }): boolean => {
  const value = config?.[countryCode];

  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';

  return false;
};

export const getSingleProductAnalytics = (product: Product) => {
  const storeCode = getProductStoreCode(product);
  const is1Pproduct = is1PMakroStore(storeCode);
  return {
    productId: getProductIdWithoutPrefix(product.id),
    sku:
      (product as Product)?.variants?.[0]?.sku !== undefined
        ? ((product as Product)?.variants?.[0]?.sku ?? '')
        : (product?.sku ?? ''),
    skuCode: product.skuCode || '',
    productType: is1Pproduct ? PRODUCT_TYPE_1P : PRODUCT_TYPE_3P,
    makroId: product?.makroId ? product.makroId : product.sku,
    variantId: product?.variants?.[0]?.id ?? '',
    ...(product?.recommendationScore !== undefined && {
      recommendationScore: product?.recommendationScore,
    }),
    ...(product?.normalisedRecommendationScore !== undefined && {
      normalisedRecommendationScore: product?.normalisedRecommendationScore,
    }),
    ...(product?.alreadyPurchased !== undefined && {
      hasAlreadyPurchased: product?.alreadyPurchased,
    }),
    ...(product?.similarityScore !== undefined && {
      similarityScore: product?.similarityScore,
    }),
    ...(product?.vectorDistance !== undefined && {
      vectorDistance: product?.vectorDistance,
    }),
    ...(product?.isSponsored !== undefined && {
      isSponsored: product?.isSponsored,
    }),
  };
};

export const getPromotionTagStyle = (type: string) => {
  let tagString = TRANSLATION_KEYS.PROPOINT.TAG;
  let desc = TRANSLATION_KEYS.PROPOINT.DESCRIPTION;
  let bgColor = PROMOTION_COLORS.PROPOINT.BACKGROUND;
  let txtColor = PROMOTION_COLORS.PROPOINT.TEXT;

  switch (type) {
    case PROPOINT_COLLECTION:
      tagString = TRANSLATION_KEYS.PROPOINT.TAG;
      desc = TRANSLATION_KEYS.PROPOINT.DESCRIPTION;
      bgColor = PROMOTION_COLORS.PROPOINT.BACKGROUND;
      txtColor = PROMOTION_COLORS.PROPOINT.TEXT;
      break;
    case ComplexPromotionType.MULTI_BUY:
      tagString = TRANSLATION_KEYS.MULTI_BUY.TAG;
      desc = TRANSLATION_KEYS.MULTI_BUY.DESCRIPTION;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
    case ComplexPromotionType.MIX_MATCH:
      tagString = TRANSLATION_KEYS.MIX_MATCH.TAG;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
    case DEFAULT:
      tagString = `loyalty.proPointPromotions.complexPromotion.${type}`;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
    default:
      tagString = `loyalty.proPointPromotions.complexPromotion.${type}`;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
  }

  return {
    tagString,
    desc,
    bgColor,
    txtColor,
  };
};

const isInjectedSearchData = (data: unknown): data is InjectedSearchData => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const { storeCodes, accessToken } = data as Record<string, unknown>;

  return (
    Array.isArray(storeCodes) &&
    storeCodes.every((code) => typeof code === 'string') &&
    typeof accessToken === 'string'
  );
};

export const getInjectedQueries = (): InjectedSearchData => {
  if (typeof window === 'undefined') return { storeCodes: [], accessToken: '' };

  const injectedObjectJson = window.ReactNativeWebView?.injectedObjectJson;

  if (!injectedObjectJson) return { storeCodes: [], accessToken: '' };

try {
  const serializedData = injectedObjectJson();

  if (!serializedData) return { storeCodes: [], accessToken: '' };

  const parsedData: unknown = JSON.parse(serializedData);

  if (!isInjectedSearchData(parsedData)) return { storeCodes: [], accessToken: '' };

  return parsedData;
} catch (error) {
  console.error('Failed to parse injected search data', error);

  return { storeCodes: [], accessToken: '' };
}
};


export interface UrlUserDetails {
  userSegments?: string[];
  isHorecaMember: boolean;
}

export const getUserDetailsFromUrl = (): UrlUserDetails => {
  if (typeof window === 'undefined') {
    return { userSegments: undefined, isHorecaMember: false };
  }

  const params = new URLSearchParams(window.location.search);
  const segmentsParam = params.get('userSegments');
  let userSegments: string[] | undefined;

  if (segmentsParam) {
    try {
      const parsedSegments: unknown = JSON.parse(segmentsParam);
      if (Array.isArray(parsedSegments)) {
        userSegments = parsedSegments.filter((segment): segment is string => typeof segment === 'string');
      }
    } catch {
      userSegments = undefined;
    }
  }

  return {
    userSegments,
    isHorecaMember: params.get('isHorecaMember')?.toLowerCase() === 'true',
  };
};
